import type { JobsiteBusinessContext, JobsiteBusinessServiceValue } from "../types/runtime-config";
import type { EditorFieldGuardrailIntent, EditorFieldMetadata } from "./editor-field-guidance";

export interface EditorFieldValidationWarning {
  kind:
    | "missingLocation"
    | "missingService"
    | "disallowedSubstring"
    | "genericCopy"
    | "tooShort"
    | "tooLong";
  message: string;
}

const SERVICE_STOP_WORDS = new Set([
  "and",
  "the",
  "for",
  "with",
  "your",
  "you",
  "our",
  "service",
  "services",
  "solutions",
  "professional",
  "professionals",
  "expert",
  "experts",
  "specialist",
  "specialists",
  "company",
  "business",
  "businesses",
  "home",
  "homes",
]);

function normalizeMatchText(value?: string): string {
  if (typeof value !== "string") return "";

  return value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeRawText(value?: string): string {
  if (typeof value !== "string") return "";
  return value.trim();
}

function getServiceLabel(service: JobsiteBusinessServiceValue): string | undefined {
  if (typeof service === "string") {
    return service.trim() || undefined;
  }

  if (service && typeof service.name === "string") {
    return service.name.trim() || undefined;
  }

  return undefined;
}

function tokenizeServiceLabel(label: string): string[] {
  return normalizeMatchText(label)
    .split(" ")
    .filter((token) => token.length >= 4 && !SERVICE_STOP_WORDS.has(token));
}

function buildServiceSignals(business?: JobsiteBusinessContext): string[] {
  if (!Array.isArray(business?.services) || business.services.length === 0) {
    return [];
  }

  const labels = business.services
    .map((service) => getServiceLabel(service))
    .filter((service): service is string => Boolean(service))
    .slice(0, 8);

  if (labels.length === 0) return [];

  if (labels.length === 1) {
    const normalizedLabel = normalizeMatchText(labels[0]);
    const distinctiveTokens = tokenizeServiceLabel(labels[0])
      .slice()
      .sort((left, right) => right.length - left.length)
      .slice(0, 2);

    return Array.from(new Set([normalizedLabel, ...distinctiveTokens])).filter(Boolean);
  }

  const tokenCounts = new Map<string, number>();

  for (const label of labels) {
    const uniqueTokens = new Set(tokenizeServiceLabel(label));
    for (const token of uniqueTokens) {
      tokenCounts.set(token, (tokenCounts.get(token) ?? 0) + 1);
    }
  }

  return Array.from(tokenCounts.entries())
    .filter(([, count]) => count >= 2)
    .sort((left, right) => {
      if (right[1] !== left[1]) return right[1] - left[1];
      return right[0].length - left[0].length;
    })
    .map(([token]) => token);
}

function containsAnySignal(value: string, signals: string[]): boolean {
  return signals.some((signal) => signal.length > 0 && value.includes(signal));
}

function buildLengthWarning(
  intent: EditorFieldGuardrailIntent | undefined,
  kind: "tooShort" | "tooLong",
): EditorFieldValidationWarning {
  if (intent === "heroSubheadline") {
    return {
      kind,
      message:
        kind === "tooShort"
          ? "This subheadline may be too short to add useful detail."
          : "This subheadline may be too long to scan quickly.",
    };
  }

  return {
    kind,
    message:
      kind === "tooShort"
        ? "This headline may be too short to explain what you do."
        : "This headline may be too long to scan quickly.",
  };
}

function buildGenericCopyWarning(
  intent: EditorFieldGuardrailIntent | undefined,
): EditorFieldValidationWarning {
  if (intent === "heroSubheadline") {
    return {
      kind: "genericCopy",
      message: "This subheadline sounds generic. Add concrete value or proof.",
    };
  }

  return {
    kind: "genericCopy",
    message: "This headline reads more like a slogan than a service headline.",
  };
}

export function getEditorFieldValidationWarnings(input: {
  value?: string;
  metadata?: EditorFieldMetadata;
  business?: JobsiteBusinessContext;
}): EditorFieldValidationWarning[] {
  const rawValue = normalizeRawText(input.value);
  const normalizedValue = normalizeMatchText(rawValue);
  const guardrails = input.metadata?.guardrails;

  if (!guardrails || normalizedValue.length === 0) {
    return [];
  }

  const warnings: EditorFieldValidationWarning[] = [];

  if (
    Array.isArray(guardrails.disallowSubstrings) &&
    guardrails.disallowSubstrings.some((substring) => rawValue.includes(substring))
  ) {
    warnings.push({
      kind: "disallowedSubstring",
      message: 'Avoid separators like "|" or ":" in this field.',
    });
  }

  if (guardrails.requireLocation) {
    const city = normalizeMatchText(input.business?.city);
    if (city && !normalizedValue.includes(city)) {
      warnings.push({
        kind: "missingLocation",
        message: `This headline no longer mentions ${input.business?.city}.`,
      });
    }
  }

  if (guardrails.requireService) {
    const serviceSignals = buildServiceSignals(input.business);
    if (serviceSignals.length > 0 && !containsAnySignal(normalizedValue, serviceSignals)) {
      warnings.push({
        kind: "missingService",
        message: "This headline no longer names a core service.",
      });
    }
  }

  if (
    Array.isArray(guardrails.genericPhrases) &&
    guardrails.genericPhrases.length > 0 &&
    !warnings.some(
      (warning) => warning.kind === "missingLocation" || warning.kind === "missingService",
    )
  ) {
    const hasGenericPhrase = guardrails.genericPhrases.some((phrase) => {
      const normalizedPhrase = normalizeMatchText(phrase);
      return normalizedPhrase.length > 0 && normalizedValue.includes(normalizedPhrase);
    });

    if (hasGenericPhrase) {
      warnings.push(buildGenericCopyWarning(guardrails.intent));
    }
  }

  if (typeof guardrails.minLength === "number" && rawValue.length < guardrails.minLength) {
    warnings.push(buildLengthWarning(guardrails.intent, "tooShort"));
  }

  if (typeof guardrails.maxLength === "number" && rawValue.length > guardrails.maxLength) {
    warnings.push(buildLengthWarning(guardrails.intent, "tooLong"));
  }

  return warnings.slice(0, guardrails.maxWarnings ?? 2);
}
